/**
 * htlifeAxios.js
 * ──────────────────────────────────────────────────────────────────────────
 * Dedicated axios instance for all HTLife service API calls in the
 * patient triage / QR registration flow.
 *
 * WHY a separate instance?
 * ─────────────────────────
 * The project's default axios (Axios-interceptor.js) is wired for the
 * doctor/admin EMR flow — it reads EMR accessToken / refreshToken from
 * sessionStorage and adds EMR-specific headers.  HTLife uses completely
 * different tokens (htlifeAccessToken / htlifeRefreshToken).  Mixing them
 * causes 401s when the EMR interceptor injects the wrong Bearer token.
 *
 * This file creates an isolated axios instance (axios.create()) that:
 *   1. Attaches htlife-specific headers on every request (request interceptor).
 *   2. On 401 → silently refreshes the htlife access token using
 *      htlifeRefreshToken stored in sessionStorage (response interceptor).
 *   3. Retries the original request once with the new token.
 *   4. Queues any concurrent 401s so only one refresh call is made.
 *
 * DOES NOT touch:
 *   • Axios-interceptor.js  (existing EMR interceptor — unchanged)
 *   • webIntegrationAxios.js (website iframe flow — unchanged)
 *   • Any other axios usage in the project
 *
 * USAGE:
 *   import htlifeAxios from "../../utils/htlifeAxios";
 *   const res = await htlifeAxios.get("get_upcomming_appointments/");
 *
 * TOKEN STORAGE (sessionStorage keys):
 *   htlifeAccessToken   — bearer token for htlife API calls
 *   htlifeRefreshToken  — used to get a new access token when it expires
 *   ORG_CODE            — sent as "Org-Code" header
 *   APP_MODE            — sent as "App-Mode" header
 */

import axios from "axios";

// ── Base URL ────────────────────────────────────────────────────────────────
// Read from .env:  REACT_APP_BACKEND_ROUTE_URL_HTLIFE=https://manage-dev.hlthtek.com/app/htlife_service/
const HTLIFE_BASE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL_HTLIFE || "";

// ── Endpoints that must NOT receive a Bearer token ──────────────────────────
// Login and register don't have a token yet; token/refresh/ must not use Bearer
// to avoid an infinite loop.
const NO_BEARER_ENDPOINTS = [
  "login/",
  "register_patient/",
  "refresh/",
];

// ── Dedicated plain axios for the refresh call ──────────────────────────────
// Using a plain axios.create() (no interceptors) so the refresh request itself
// never triggers another 401 → refresh loop.
const refreshAxios = axios.create();

// ── Token expiry helper ──────────────────────────────────────────────────────
const isExpiredOrExpiring = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    // Consider expired if within 60 seconds of expiry
    return payload.exp * 1000 < Date.now() + 60000;
  } catch {
    return true;
  }
};

/**
 * getValidToken()
 * ───────────────
 * Returns a guaranteed-valid access token for use in manual/proactive calls.
 * - If the stored token is still valid → returns it directly (no network call).
 * - If expired/expiring → refreshes first, saves new token, returns it.
 * - If refresh also fails → clears tokens, fires "htlife-session-expired",
 *   returns null (caller must abort and show login).
 */
export async function getValidToken() {
  const current = sessionStorage.getItem("htlifeAccessToken") || "";
  if (current && !isExpiredOrExpiring(current)) return current;

  const orgCode = sessionStorage.getItem("ORG_CODE") || "";
  const appMode = sessionStorage.getItem("APP_MODE") || "";

  try {
    const newToken = await callRefreshEndpoint(orgCode, appMode);
    if (newToken) return newToken;
  } catch {
    // fall through
  }

  sessionStorage.removeItem("htlifeAccessToken");
  sessionStorage.removeItem("htlifeRefreshToken");
  window.dispatchEvent(new CustomEvent("htlife-session-expired"));
  return null;
}

// ── Main htlife axios instance ───────────────────────────────────────────────
const htlifeAxios = axios.create({
  baseURL: HTLIFE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ════════════════════════════════════════════════════════════════════════════
// REQUEST INTERCEPTOR
// Runs before every htlifeAxios call.
// Reads the latest token + org headers from sessionStorage and injects them.
// Reading from sessionStorage each time (not a closure variable) ensures
// that a freshly refreshed token is picked up automatically.
// ════════════════════════════════════════════════════════════════════════════
htlifeAxios.interceptors.request.use(
  (config) => {
    if (!config.headers) config.headers = {};

    const url = config.url || "";

    // When the body is FormData (e.g. voice assessment file upload), let the
    // browser set Content-Type automatically so it includes the correct boundary.
    // The hardcoded "application/json" default would otherwise override it.
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    // Add Org-Code and App-Mode on every request (required by htlife service).
    const orgCode = sessionStorage.getItem("ORG_CODE") || "";
    const appMode = sessionStorage.getItem("APP_MODE") || "";
    if (orgCode) config.headers["Org-Code"] = orgCode;
    if (appMode) config.headers["App-Mode"] = appMode;

    // Add Authorization header only for protected endpoints.
    const skipBearer = NO_BEARER_ENDPOINTS.some((ep) => url.includes(ep));
    if (!skipBearer) {
      const token = sessionStorage.getItem("htlifeAccessToken") || "";
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ════════════════════════════════════════════════════════════════════════════
// REFRESH QUEUE HELPERS
// When multiple API calls fire at the same time and all get 401 (e.g. after
// token expires), only the first one should call token/refresh/.
// The rest queue here and wait for the new token.
// ════════════════════════════════════════════════════════════════════════════
let isRefreshing = false;          // true while a refresh call is in flight
let refreshSubscribers = [];       // queued request callbacks waiting for new token

/**
 * Called once the refresh succeeds or fails.
 * Resolves/rejects all queued requests with the new token (or null on failure).
 */
function onRefreshed(newAccessToken) {
  refreshSubscribers.forEach((cb) => cb(newAccessToken));
  refreshSubscribers = [];
}

/**
 * Queues a request to be retried after the refresh completes.
 * Returns a Promise that resolves with the retry response.
 */
function queueRequest(originalRequest) {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push((newToken) => {
      if (!newToken) {
        // Refresh failed — reject all queued requests
        reject(new Error("HTLife token refresh failed"));
        return;
      }
      // Retry with the new token
      originalRequest._htlifeRetry = true;
      originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
      resolve(htlifeAxios(originalRequest));
    });
  });
}

/**
 * Calls the HTLife token/refresh/ endpoint using the plain refreshAxios
 * (no interceptors) to avoid triggering this same response interceptor.
 */
async function callRefreshEndpoint(orgCode, appMode) {
  const refreshToken = sessionStorage.getItem("htlifeRefreshToken") || "";
  if (!refreshToken) return null;

  if (!orgCode) orgCode = sessionStorage.getItem("ORG_CODE") || "";
  if (!appMode) appMode = sessionStorage.getItem("APP_MODE") || "";

  const headers = { "Content-Type": "application/json" };
  if (orgCode) headers["Org-Code"] = orgCode;
  if (appMode) headers["App-Mode"] = appMode;

  const res = await refreshAxios.post(
    `${HTLIFE_BASE_URL}refresh/`,
    JSON.stringify({ refresh: refreshToken }),
    { headers }
  );

  // HTLife may return the new access token at different paths — handle both.
  const newAccess = res?.data?.access || res?.data?.data?.access || "";
  const newRefresh = res?.data?.refresh || res?.data?.data?.refresh || "";

  if (newAccess) {
    // Persist the new tokens so subsequent requests use them.
    sessionStorage.setItem("htlifeAccessToken", newAccess);
    if (newRefresh) {
      sessionStorage.setItem("htlifeRefreshToken", newRefresh);
    }
  }

  return newAccess || null;
}

// ════════════════════════════════════════════════════════════════════════════
// RESPONSE INTERCEPTOR
// Runs after every htlifeAxios response.
// On 401 → refresh the htlife access token and retry the original request.
// ════════════════════════════════════════════════════════════════════════════
htlifeAxios.interceptors.response.use(
  // Success path — pass through unchanged.
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 Unauthorized.
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // _htlifeRetry flag prevents an infinite loop:
    // if the retried request also returns 401, reject immediately.
    if (!originalRequest || originalRequest._htlifeRetry) {
      return Promise.reject(error);
    }

    // Don't try to refresh for login or register endpoints — they're not
    // Bearer-protected and shouldn't have a refresh token yet.
    const url = originalRequest.url || "";
    const isPublicEndpoint = NO_BEARER_ENDPOINTS.some((ep) => url.includes(ep));
    if (isPublicEndpoint) {
      return Promise.reject(error);
    }

    // If a refresh is already in progress, queue this request.
    // It will be retried automatically once the refresh completes.
    if (isRefreshing) {
      return queueRequest(originalRequest);
    }

    // This is the first 401 — start the refresh flow.
    isRefreshing = true;

    try {
      const newToken = await callRefreshEndpoint();

      isRefreshing = false;

      if (newToken) {
        // Refresh succeeded — notify queued requests and retry this one.
        onRefreshed(newToken);
        originalRequest._htlifeRetry = true;
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return htlifeAxios(originalRequest);
      } else {
        // Refresh returned no token (refresh token expired/invalid).
        // Clear stale tokens and notify the app so it can redirect to login.
        onRefreshed(null);
        sessionStorage.removeItem("htlifeAccessToken");
        sessionStorage.removeItem("htlifeRefreshToken");
        window.dispatchEvent(new CustomEvent("htlife-session-expired"));
        return Promise.reject(error);
      }
    } catch (refreshError) {
      // Refresh call itself threw an error (network issue, 400, etc.).
      isRefreshing = false;
      onRefreshed(null);
      sessionStorage.removeItem("htlifeAccessToken");
      sessionStorage.removeItem("htlifeRefreshToken");
      window.dispatchEvent(new CustomEvent("htlife-session-expired"));
      return Promise.reject(refreshError);
    }
  }
);

export default htlifeAxios;
